const API_BASE_URL = 'https://api.quali-d.com'

export class AuthService {
  /**
   * Generate a browser fingerprint hash for session tracking.
   * Matches the same algorithm used in AgentLoginView.vue.
   */
  static generateDeviceFingerprint () {
    try {
      const components = [
        navigator.userAgent,
        navigator.language,
        screen.colorDepth,
        `${screen.width}x${screen.height}`,
        new Date().getTimezoneOffset(),
        !!window.sessionStorage,
        !!window.localStorage
      ]
      const raw = components.join('|||')
      // Simple hash (same as agent flow)
      let hash = 0
      for (let i = 0; i < raw.length; i++) {
        const char = raw.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash
      }
      return Math.abs(hash).toString(16).padStart(8, '0')
    } catch (_) {
      return 'unknown'
    }
  }

  /**
   * Return a human-readable device/browser name.
   * Matches the same algorithm used in AgentLoginView.vue.
   */
  static getDeviceName () {
    try {
      const ua = navigator.userAgent
      let browser = 'Browser'
      let os = 'Unknown OS'

      if (ua.includes('Firefox')) browser = 'Firefox'
      else if (ua.includes('Edg')) browser = 'Edge'
      else if (ua.includes('Chrome')) browser = 'Chrome'
      else if (ua.includes('Safari')) browser = 'Safari'
      else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera'

      if (ua.includes('Windows')) os = 'Windows'
      else if (ua.includes('Mac')) os = 'macOS'
      else if (ua.includes('Linux')) os = 'Linux'
      else if (ua.includes('Android')) os = 'Android'
      else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'

      return `${browser} on ${os}`
    } catch (_) {
      return 'Web Browser'
    }
  }

  static async login (credentials) {
    try {
      const body = {
        country_code: credentials.country_code,
        phone: credentials.phone,
        password: credentials.password,
        platform: 'web',
        device_fingerprint: this.generateDeviceFingerprint(),
        device_name: this.getDeviceName()
      }

      if (credentials.cf_turnstile_response) {
        body.cf_turnstile_response = credentials.cf_turnstile_response
      }

      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(body)
      })

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned an unexpected response. Please try again.')
      }

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Login failed')
      }

      const isSuccess = data.status === 'success' || data.success === true
      if (!isSuccess) {
        throw new Error(data.message || 'Invalid credentials')
      }

      // TOTP required — return early, real token will come after code verification
      if (data.totp_required === true) {
        return data
      }

      if (data.success && data.token) {
        data.user = {
          id: data.user_id,
          name: data.name,
          phone: data.phone,
          email: data.email,
          role: data.role_name,
          role_id: data.role_id,
          roleId: data.role_id,
          company: data.company_name,
          plan: data.plan_name,
          planId: data.plan_id,
          country_code: data.country_code
        }
      }

      return data
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Unable to connect to the server.')
      }
      throw error
    }
  }

  static setAuthData (authData) {
    if (authData.token) {
      localStorage.setItem('auth_token', authData.token)
      localStorage.setItem('token', authData.token)
    }
    if (authData.session_key) {
      localStorage.setItem('user_session_key', authData.session_key)
    }
    if (authData.user) {
      localStorage.setItem('user_data', JSON.stringify(authData.user))
      localStorage.setItem('user', JSON.stringify(authData.user))
    }
  }

  static getAuthToken () {
    return localStorage.getItem('auth_token')
  }

  static getUserData () {
    const userData = localStorage.getItem('user_data')
    return userData ? JSON.parse(userData) : null
  }

  static isAuthenticated () {
    return !!this.getAuthToken()
  }

  static logout () {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('user_session_key')
  }

  /**
   * Poll the backend to check if this session is still active.
   * Returns { active: true } if valid, { active: false } if kicked.
   * Returns { active: true } if no session_key exists (legacy/non-tracked login).
   */
  static async checkSession () {
    const token = this.getAuthToken()
    const sessionKey = localStorage.getItem('user_session_key')

    if (!token || !sessionKey) return { active: true }

    try {
      const res = await fetch(
        `${API_BASE_URL}/check-session?session_key=${encodeURIComponent(sessionKey)}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        }
      )
      if (!res.ok) return { active: true } // Network/server error — don't kick
      return res.json()
    } catch (_) {
      return { active: true } // Network error — don't kick on connectivity issues
    }
  }

  /**
   * Register a session for an already-logged-in user (no logout required).
   * Called on dashboard mount when auth_token exists but user_session_key doesn't.
   * Kicks oldest session if over the limit — same FIFO logic as fresh login.
   */
  static async registerSession () {
    const token = this.getAuthToken()
    if (!token) return

    try {
      const res = await fetch(`${API_BASE_URL}/session/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        body: JSON.stringify({
          platform: 'web',
          device_fingerprint: this.generateDeviceFingerprint(),
          device_name: this.getDeviceName()
        })
      })
      if (!res.ok) return
      const data = await res.json()
      if (data.session_key) {
        localStorage.setItem('user_session_key', data.session_key)
      }
    } catch (_) {
      // Network error — silently skip, user stays untracked until next mount
    }
  }

  static async makeAuthenticatedRequest (url, options = {}) {
    const token = this.getAuthToken()

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    return fetch(url, { ...options, headers })
  }

  static async getUserCredits () {
    try {
      const token = this.getAuthToken()
      if (!token) throw new Error('No authentication token found')

      const response = await fetch('https://dashboard.quali-d.com/api/get_my_credit.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({ token })
      })

      if (!response.ok) throw new Error('Failed to fetch user credits')

      const data = await response.json()

      if (Array.isArray(data) && data.length > 0) {
        return { credits: data[0].minutes || 0, id: data[0].id }
      }

      return { credits: 0, id: null }
    } catch (error) {
      return { credits: 0, id: null, error: error.message }
    }
  }
}
